


import { useState } from "react"
import styled, { keyframes, ThemeProvider } from "styled-components"
import { darkTheme } from "./Themes"
import { motion } from "framer-motion"

import LogoComponent from "../subComponents/LogoComponent"
import SocialIcons from "../subComponents/SocialIcons"
import PowerButton from "../subComponents/PowerButton"
import ParticleComponent from "../subComponents/ParticleComponent"

import Monkey from "../assets/Images/contact_spaceman-removebg-preview.png"
import BigTitle from "../subComponents/BigTitle"

const Box = styled.div`
  background-color: ${(props) => props.theme.body};
  width: 100vw;
  height: 100vh;
  position: relative;
  overflow: hidden;
`

const float = keyframes`
  0% { transform: translateY(-10px); }
  50% { transform: translateY(15px) translateX(15px); }
  100% { transform: translateY(-10px); }
`

const Spaceman = styled.div`
  position: absolute;
  top: 10%;
  right: 5%;
  width: 20vw;
  animation: ${float} 4s ease infinite; 

  img {
    width: 100%;
    height: auto;
    cursor: pointer;
  }

  @media (max-width: 768px) {
    width: 30vw;
    top: 5%;
    right: 5%;
  }
`

const Main = styled.div`
  border: 2px solid ${(props) => props.theme.text};
  color: ${(props) => props.theme.text};
  padding: 2rem;
  width: 50vw;
  height: 60vh;
  z-index: 3;
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: calc(0.6rem + 1vw);
  backdrop-filter: blur(4px);
  position: absolute;
  left: calc(5rem + 5vw);
  top: 55%;
  transform: translateY(-50%);
  font-family: 'Ubuntu Mono', monospace;
  font-style: italic;

  @media (max-width: 768px) {
    width: 80vw;
  height: auto;

    left: 50%;
    transform: translate(-50%, -50%);
    padding: 1.5rem;
    font-size: calc(0.5rem + 1vw);
  }
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  
  p {
    margin-bottom: 0.5rem;
    font-weight: bold;
  }

  input, textarea {
    padding: 0.75rem;
    margin-bottom: 1rem;
    border: 1px solid ${(props) => props.theme.text};
    background: transparent;
    color: ${(props) => props.theme.text};
    font-size: 1rem;
    font-family: 'Ubuntu Mono', monospace;
    border-radius: 4px;
    transition: all 0.3s ease;

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px ${(props) => props.theme.text};
    }
  }

  textarea {
    height: 100px;
    resize: vertical;
  }

  button {
    padding: 0.75rem;
    border: none;
    background-color: ${(props) => props.theme.text};
    color: ${(props) => props.theme.body};
    cursor: pointer;
    font-size: 1rem;
    font-weight: bold;
    transition: all 0.3s ease;
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: center;

    &:hover {
      background-color: ${(props) => props.theme.body};
      color: ${(props) => props.theme.text};
      border: 1px solid ${(props) => props.theme.text};
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }
`

const Message = styled.p`
  color: ${(props) => (props.success ? "green" : "red")};
  font-size: 1rem;
  text-align: center;
  margin-top: 1rem;
`

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid ${(props) => props.theme.text};
  border-radius: 50%;
  border-top: 2px solid ${(props) => props.theme.body};
  animation: spin 1s linear infinite;
  margin-right: 10px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

const pageVariants = {
  initial: { opacity: 0, y: -50 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  exit: { opacity: 0, y: 50, transition: { duration: 0.5 } },
}

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    message: "",
  })

  const [status, setStatus] = useState({ success: null, message: "" })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const response = await fetch("https://edwardndiyoo.pythonanywhere.com/send_contact_form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      })

      const result = await response.json()

      if (response.ok) {
        setStatus({ success: true, message: "Message sent successfully!" })
        setFormData({ fullName: "", email: "", message: "" })
      } else {
        setStatus({ success: false, message: result.error || "Failed to send message." })
      }
    } catch (error) {
      setStatus({ success: false, message: "Error sending message. Please try again." })
    } finally {
      setIsSubmitting(false)

       // Reset the status message after 3-5 seconds
       setTimeout(() => {
        setStatus({ success: null, message: "" })
      }, 3000) // Set 3000 ms (3 seconds) as the delay
    }
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <motion.div initial="initial" animate="animate" exit="exit" variants={pageVariants}>
        <Box>
          <LogoComponent theme="dark" />
          <SocialIcons theme="dark" />
          <PowerButton />
          <ParticleComponent theme="dark" />

          <Spaceman>
            <img src={Monkey || "/placeholder.svg"} alt="Contact-img" />
          </Spaceman>

          <Main>
            <Form onSubmit={handleSubmit}>
              <p>Full Name</p>
              <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} required />
              <p>Email</p>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required />
              <p>Message</p>
              <textarea name="message" value={formData.message} onChange={handleChange} required />
              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <LoadingSpinner />
                    Sending...
                  </>
                ) : (
                  "Send Message"
                )}
              </button>
            </Form>
            {status.message && <Message success={status.success}>{status.message}</Message>}
          </Main>

          <BigTitle text="CONTACT" top="10%" left="5%" />
        </Box>
      </motion.div>
    </ThemeProvider>
  )
}

export default Contact

